<template>
  <div class="base-radar-chart">
    <canvas class="chart" ref="chart"/>
    <div class="labels" ref="labels">
      <BaseText5
        class="label"
        v-for="label in data.labels"
        :key="label"
        :text="label"
      />
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import BaseText5 from '@/components/BaseText5/BaseText5'
export default {
  components: {
    BaseText5
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    animationDuration: {
      type: Number,
      default: 500
    }
  },
  mounted () {
    this.chart = new Chart(this.$refs['chart'], {
      type: 'radar',
      data: {
        labels: this.data.labels,
        datasets: this.createDatasets(this.data.datasets)
      },
      options: {
        animation: {
          duration: this.animationDuration,
          easing: 'linear'
        },
        maintainAspectRatio: true,
        aspectRatio: 1,
        scale: {
          ticks: { min: 0, max: 1, stepSize: 1 / this.gridLineAmount, display: false },
          gridLines: this.gridLines,
          angleLines: { color: this.gridLineColor, lineWidth: this.gridLineWidth },
          pointLabels: { display: false, fontSize: 10, fontColor: 'rgba(255, 255, 255, 1)', fontFamily: 'Klavika Bold Italic', lineHeight: '25px' }
        },
        layout: { padding: this.gridBorderWidth },
        legend: { display: false },
        tooltips: { enabled: false }
      }
    })
    this.positionLabels()
  },
  data () {
    return {
      gridLineAmount: 3,
      gridBorderColor: 'white',
      gridBorderWidth: 3,
      gridLineColor: 'rgba(255, 255, 255, 0.1)',
      gridLineWidth: 1,
      datasetStyles: {
        0: {
          fill: true,
          borderWidth: 2,
          borderCapStyle: 'round',
          borderJoinStyle: 'round',
          backgroundColor: 'transparent',
          borderColor: '#00b7ff',
          pointRadius: 3,
          pointBorderWidth: 1,
          pointBorderColor: 'white',
          pointBackgroundColor: ''
        },
        1: {
          fill: true,
          borderWidth: 4,
          borderCapStyle: 'round',
          borderJoinStyle: 'round',
          backgroundColor: 'rgba(253, 31, 51, 0.4)',
          borderColor: '#fd1f33',
          pointRadius: 3,
          pointBorderWidth: 1,
          pointBorderColor: 'white',
          pointBackgroundColor: ''
        }
      }
    }
  },
  computed: {
    gridLines () {
      const gridlines = { color: [], lineWidth: [], circular: true }
      for (let i = 0; i < this.gridLineAmount; i++) {
        const isLast = i === this.gridLineAmount - 1
        gridlines.color.push(isLast ? this.gridBorderColor : this.gridLineColor)
        gridlines.lineWidth.push(isLast ? this.gridBorderWidth : this.gridLineWidth)
      }
      return gridlines
    }
  },
  watch: {
    data (data) {
      this.chart.data.datasets.forEach((dataset, i) => (dataset.data = data.datasets[i]))
      this.chart.update()
    }
  },
  methods: {
    createDatasets (datasets) {
      return datasets.map((dataset, i) => ({ data: dataset, ...this.datasetStyles[i] }))
    },
    positionLabels () {
      const parent = this.$refs['labels']
      const radius = parseFloat(window.getComputedStyle(parent).height) / 2
      Array.from(parent.children).forEach((el, i) => {
        const theta = 360 / parent.children.length / 180 * i * Math.PI
        const degrees = theta * 180 / Math.PI
        let translateX = 'translateX(-50%)'
        let translateY = 'translateY(-50%)'
        if (degrees > 0 && degrees < 180) translateX = 'translateX(0%)'
        if (degrees > 180 && degrees < 360) translateX = 'translateX(-100%)'
        if (degrees < 90 || degrees > 270) translateY = 'translateY(-100%)'
        if (degrees > 90 && degrees < 270) translateY = 'translateY(0%)'
        el.style.position = 'absolute'
        el.style.top = (radius - (radius * Math.cos(theta))) / (radius * 2) * 100 + '%'
        el.style.left = (radius + (radius * Math.sin(theta))) / (radius * 2) * 100 + '%'
        el.style.transform = `${translateX} ${translateY}`
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.base-radar-chart {
  width: 100%;
  position: relative;
}
.chart {
  width: 100%;
  height: 100%;
}
.labels {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}
.label {
  padding: 0.5em;
}
</style>
